import { PointLight, Quaternion, SpotLight, Vector3 } from 'three';
import { Entity } from './Entity.js';
import { VehicleSmoke } from './VehicleSmoke.js';
import { createHilux } from './vehicles/Hilux.js';
import { GROUP, MASK } from '../physics/groups.js';
import { clamp, damp, lerp, shortestAngle } from '../utils/math.js';

const UP = new Vector3(0, 1, 0);

/**
 * Player vehicle — a rigid chassis driven by Ammo's raycast vehicle.
 *
 * Bullet owns the wheel raycasts, suspension and tyre friction; this class
 * only feeds it pedals and steering, then copies the chassis and wheel
 * transforms onto the meshes. Drive/steer/brake forces and the suspension
 * geometry all live in `config.vehicle`.
 *
 * The visual root (`this.mesh`) is synced to the chassis body, so wheels are
 * its children and their positions are converted into chassis space.
 */
export class Vehicle extends Entity {
  constructor({ physics, input, config, palette, scene, spawn, spawnYaw = 0 }) {
    super();

    this.physics = physics;
    this.input = input;
    this.config = config;

    this.spawn = new Vector3(spawn[0], spawn[1], spawn[2]);
    // Gameplay convention: yaw 0 means "facing away from the start camera",
    // which is -Z. The model's nose points along its local +Z.
    this.initialYaw = Math.PI + spawnYaw;

    this.steer = 0;
    this.spin = 0;
    this.speed = 0;
    this.speedKmh = 0;
    this.heading = this.initialYaw;
    this.reversing = false;
    this.braking = false;
    this.onRespawn = null;
    this.onFuelEmpty = null;
    this.onDamaged = null;
    this.onHealthEmpty = null;

    // Fuel is a 0..1 tank fraction. Game owns refuelling (it knows where the
    // station forecourts are); the vehicle owns consumption as it moves.
    this.fuel = clamp(config.fuel?.startFraction ?? 1, 0, 1);

    // Collision health is separate from fuel. Traffic impacts remove a fixed
    // chunk; a short per-car cooldown keeps one contact from draining the bar
    // over consecutive physics frames.
    this.maxHealth = Math.max(1, config.health?.max ?? 100);
    this.health = this.maxHealth;
    this._simTime = 0;
    this._lastCarHit = new Map();

    this._flippedTime = 0;
    this._quat = new Quaternion();
    this._forward = new Vector3();
    this._velocity = new Vector3();
    this._up = new Vector3();
    this._impulse = new Vector3();
    this._wheelWorld = new Vector3();
    this._wheelLocal = new Vector3();
    this._inverseQuat = new Quaternion();

    /** Read-only view for the camera rig. Updated in `lateUpdate` and `render`. */
    this.focus = { position: new Vector3(), heading: this.heading, speed: 0 };

    const { group, wheels, materials } = createHilux({
      colors: palette,
      wheel: config.wheel,
      plate: config.plate,
    });

    this.mesh = group;
    this.wheels = wheels;
    this.lights = materials;
    for (const wheel of wheels) this.mesh.add(wheel);

    this.#buildHeadlights();

    // Engine damage plume. World-space so the puffs trail the moving truck.
    this.smoke = new VehicleSmoke({
      parent: scene,
      config: config.health?.smoke,
    });

    this.body = physics.addBody({
      shape: {
        type: 'compound',
        parts: [
          { type: 'box', size: config.chassis },
          {
            type: 'box',
            size: config.cabin,
            position: config.cabinOffset,
          },
          {
            type: 'box',
            size: config.bed,
            position: config.bedOffset,
          },
        ],
      },
      mass: config.mass,
      position: spawn,
      quaternion: this.#quaternionFor(this.initialYaw),
      mesh: this.mesh,
      friction: 0.5,
      restitution: 0.05,
      linearDamping: 0.03,
      angularDamping: 0.45,
      disableDeactivation: true,
      group: GROUP.VEHICLE,
      mask: MASK.VEHICLE,
      userData: { type: 'vehicle', entity: this },
    });

    this.#initVehicle();

    // Interpolation buffer. Bullet steps at a fixed 60 Hz, but the renderer can
    // run at 120/144 Hz. `lateUpdate` stores the previous and current physics
    // poses; `render(alpha)` blends them so the truck moves on every displayed
    // frame instead of jumping once every physics step.
    this._prevPosition = new Vector3();
    this._currPosition = new Vector3();
    this._prevQuaternion = new Quaternion();
    this._currQuaternion = new Quaternion();
    this._prevWheelY = wheels.map(() => 0);
    this._currWheelY = wheels.map(() => 0);
    this._prevSpin = 0;
    this._currSpin = 0;
    this._prevSteer = 0;
    this._currSteer = 0;
    this._prevSpeed = 0;
    this._currSpeed = 0;
    this._prevHeading = this.initialYaw;
    this._currHeading = this.initialYaw;

    this._prevPosition.copy(this.spawn);
    this._currPosition.copy(this.spawn);
    this._prevQuaternion.copy(this.mesh.quaternion);
    this._currQuaternion.copy(this.mesh.quaternion);

    this.focus.position.copy(this.spawn);
  }

  #quaternionFor(yaw) {
    return new Quaternion().setFromAxisAngle(UP, yaw).toArray();
  }

  // Two forward spotlights plus a soft cabin glow, parented to the truck so they
  // track it. Intensities are driven by the day/night cycle from Game each frame.
  #buildHeadlights() {
    const make = (x) => {
      const spot = new SpotLight(0xfff2d6, 0, 62, Math.PI / 7, 0.5, 1.5);
      spot.position.set(x, 0.55, 2.0); // front bumper, local +Z is the nose
      spot.target.position.set(x, 0.0, 16);
      this.mesh.add(spot);
      this.mesh.add(spot.target);
      return spot;
    };
    this.headlights = [make(0.62), make(-0.62)];

    this.cabinLight = new PointLight(0xffd9a0, 0, 7, 2);
    this.cabinLight.position.set(0, 0.75, 0.3);
    this.mesh.add(this.cabinLight);
  }

  /** night: 0 (full day) .. 1 (deep night). blackout boosts the cones so a
   *  couple of metres of road still read when the whole grid has failed. */
  setHeadlightLevel(night, blackout = 0) {
    const n = Math.max(0, Math.min(1, night));
    const b = Math.max(0, Math.min(1, blackout));
    const spot = n * 140 + b * 55;
    for (const h of this.headlights) {
      h.intensity = spot;
      h.distance = 62 + b * 16;
    }
    this.cabinLight.intensity = n * 16 + b * 12;
  }

  #initVehicle() {
    const Ammo = this.physics.Ammo;
    const { suspension, wheel, wheels, driven } = this.config;

    this.tuning = new Ammo.btVehicleTuning();
    this.tuning.set_m_suspensionStiffness(suspension.stiffness);
    this.tuning.set_m_suspensionCompression(suspension.compression);
    this.tuning.set_m_suspensionDamping(suspension.damping);
    this.tuning.set_m_maxSuspensionTravelCm(suspension.travel * 100);
    this.tuning.set_m_maxSuspensionForce(suspension.maxForce);
    this.tuning.set_m_frictionSlip(suspension.frictionSlip);

    this.raycaster = new Ammo.btDefaultVehicleRaycaster(this.physics.world);
    this.vehicle = new Ammo.btRaycastVehicle(this.tuning, this.body.native, this.raycaster);
    this.vehicle.setCoordinateSystem(0, 1, 2); // x right, y up, z forward

    const direction = new Ammo.btVector3(0, -1, 0);
    const axle = new Ammo.btVector3(-1, 0, 0);

    for (const entry of wheels) {
      const connection = new Ammo.btVector3(entry.x, suspension.connectionHeight, entry.z);
      this.vehicle.addWheel(
        connection,
        direction,
        axle,
        suspension.restLength,
        wheel.radius,
        this.tuning,
        entry.front,
      );
      Ammo.destroy(connection);

      const info = this.vehicle.getWheelInfo(this.vehicle.getNumWheels() - 1);
      info.set_m_rollInfluence(suspension.rollInfluence);
    }

    Ammo.destroy(direction);
    Ammo.destroy(axle);

    this.drivenWheels = wheels
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => {
        if (driven === 'all') return true;
        if (driven === 'rear') return !entry.front;
        return entry.front;
      })
      .map(({ index }) => index);

    this.physics.addAction(this.vehicle);
  }

  get isAirborne() {
    const count = this.vehicle.getNumWheels();
    for (let i = 0; i < count; i += 1) {
      if (this.vehicle.getWheelInfo(i).get_m_raycastInfo().get_m_isInContact()) return false;
    }
    return true;
  }

  fixedUpdate(dt) {
    this._simTime += dt;

    if (this.input.consumePressed('reset')) {
      this.respawn();
      return;
    }

    this.#applyControls(dt);

    // Extra downforce at speed keeps the truck planted without touching the
    // suspension tuning.
    const speedAbs = Math.abs(this.speed);
    if (speedAbs > 3) {
      const downforce = this.config.downforce * Math.min(speedAbs, 30) * dt;
      this._impulse.set(0, -downforce, 0);
      this.body.applyCentralImpulse(this._impulse);
    }

    // Flipped over for a while? Put it back on its wheels.
    this.body.getRotation(this._quat);
    this._up.copy(UP).applyQuaternion(this._quat);
    if (this._up.y < -0.35) {
      this._flippedTime += dt;
      if (this._flippedTime > this.config.flipResetTime) this.respawn();
    } else {
      this._flippedTime = 0;
    }

    this.#burnFuel(dt);
  }

  /**
   * Distance-based fuel burn. It uses planar velocity rather than throttle so
   * coasting and sliding still consume fuel, while a suspension bounce or a
   * short drop does not. The small load multiplier makes stop-start driving
   * and reversing a little thirstier than a steady cruise.
   */
  #burnFuel(dt) {
    if (this.fuel <= 0) return;

    this.body.getLinearVelocity(this._velocity);
    const distance = Math.hypot(this._velocity.x, this._velocity.z) * dt;
    if (distance <= 1e-5) return;

    const underLoad = Math.abs(this.input.throttleAxis) > 0.06;
    const load = underLoad ? this.config.fuel.loadMultiplier : 1;
    const burn = this.config.fuel.burnPerKm * (distance / 1000) * load;
    if (burn <= 0) return;

    this.fuel = Math.max(0, this.fuel - burn);
    if (this.fuel <= 0) {
      this.fuel = 0;
      this.onFuelEmpty?.();
    }
  }

  /** Add fuel on a 0..1 scale. Used by the station refuelling interaction. */
  addFuel(amount) {
    if (!(amount > 0)) return;
    this.fuel = clamp(this.fuel + amount, 0, 1);
  }

  /** Replace the tank contents, e.g. on a fresh start after game over. */
  refill(fraction = 1) {
    this.fuel = clamp(fraction, 0, 1);
  }

  /**
   * Register one traffic-car impact. The cooldown is keyed by car record so
   * simultaneous hits from different cars still each cost health.
   */
  collideWithCar(car, context = {}) {
    if (!car || this.health <= 0) return false;

    const cooldown = this.config.health?.hitCooldown ?? 0.5;
    const lastHit = this._lastCarHit.get(car);
    if (lastHit !== undefined && this._simTime - lastHit < cooldown) return false;

    this._lastCarHit.set(car, this._simTime);
    this.damage(this.config.health?.collisionDamage ?? 5, {
      ...context,
      car,
    });
    return true;
  }

  /** Apply damage and notify the game; health is clamped at zero. */
  damage(amount, context = {}) {
    if (!(amount > 0) || this.health <= 0) return 0;

    const before = this.health;
    this.health = Math.max(0, this.health - amount);
    const applied = before - this.health;

    this.onDamaged?.({
      ...context,
      health: this.health,
      maxHealth: this.maxHealth,
      damage: applied,
    });
    if (this.health <= 0) this.onHealthEmpty?.();
    return applied;
  }

  /**
   * Give health back, e.g. after a jabana at a tea stand. Returns the amount
   * actually restored, so a truck at full health reports zero.
   */
  heal(amount) {
    if (!(amount > 0) || this.health <= 0) return 0;

    const before = this.health;
    this.health = Math.min(this.maxHealth, this.health + amount);
    return this.health - before;
  }

  /** Restore collision health for a fresh run. */
  repair() {
    this.health = this.maxHealth;
    this._lastCarHit.clear();
    this.smoke?.reset();
  }

  /**
   * Throttle demand for auto-drive.
   *
   * A proportional governor rather than a switch: it asks for full throttle
   * when it is well short of the cruise speed and backs off as it closes, so
   * the truck settles instead of surging. The demand is damped as well, or it
   * would chirp the tyres off the line every time the player shifts to D.
   */
  #autoThrottle(forwardSpeed, dt) {
    const { config } = this;
    const cruise =
      this.input.gear === 'R'
        ? -config.maxReverseSpeed * 0.8
        : config.maxSpeed * 0.62;

    const error = cruise - forwardSpeed;
    // Four metres a second short is full throttle; inside the dead band it
    // coasts and lets rolling resistance close the last of the gap.
    const target = Math.abs(error) < 0.35 ? 0 : clamp(error / 4, -1, 1);
    this._autoPedal = damp(this._autoPedal ?? 0, target, 5, dt);
    return this._autoPedal;
  }

  #applyControls(dt) {
    const { input, config } = this;

    // Both the keyboard and the on-screen stick land here as continuous axes:
    // keys give ±1, the stick gives whatever the thumb is asking for.
    const steerInput = input.steerAxis;
    const handbrake = input.isDown('handbrake');

    // Forward speed in the chassis frame.
    this.body.getRotation(this._quat);
    this._forward.set(0, 0, 1).applyQuaternion(this._quat);
    this.body.getLinearVelocity(this._velocity);
    const forwardSpeed = this._velocity.dot(this._forward);

    // Auto-drive replaces the pedals with a governor: the player picks a
    // direction with the shifter and the truck holds a cruise speed in it.
    // It still runs through the same engine/brake path below, so traction,
    // fuel, handbrake and collisions behave exactly as they do on the pedals.
    const throttleAxis = input.autoDrive
      ? this.#autoThrottle(forwardSpeed, dt)
      : input.throttleAxis;

    const throttle = throttleAxis > 0.06;
    const pedal = throttleAxis < -0.06;

    let engineForce = 0;
    let brake = 0;
    this.reversing = false;
    this.braking = false;

    if (throttle && !pedal) {
      if (forwardSpeed < -0.4) {
        brake = config.brakeForce;
        this.braking = true;
      } else {
        const headroom = 1 - clamp(forwardSpeed / config.maxSpeed, 0, 1);
        engineForce =
          config.engineForce * throttleAxis * (0.3 + 0.7 * headroom);
      }
    } else if (pedal || (throttle && pedal)) {
      const reverseDemand = Math.min(1, -throttleAxis);
      if (forwardSpeed > 0.4 || throttle) {
        // Squeeze the pedal halfway and you get half the braking.
        brake = config.brakeForce * (0.45 + 0.55 * reverseDemand);
        this.braking = true;
      } else {
        const headroom = 1 - clamp(-forwardSpeed / config.maxReverseSpeed, 0, 1);
        engineForce = -config.reverseForce * reverseDemand * (0.35 + 0.65 * headroom);
        this.reversing = true;
      }
    }

    if (handbrake) {
      engineForce = 0;
      this.braking = true;
    }

    // The auto-drive brake pedal overrides the governor: it is a foot brake,
    // so it cuts the throttle and stands the truck still without ever asking
    // for reverse. The governor's stored pedal is zeroed as well, or letting
    // go would re-apply full throttle from a standstill and lurch.
    if (input.autoDrive && input.brakeHeld) {
      engineForce = 0;
      brake = config.brakeForce;
      this.braking = true;
      this.reversing = false;
      this._autoPedal = 0;
    }

    // A dry tank cuts the engine. The game-over state normally freezes the
    // simulation on the same frame, but this keeps the vehicle safe to step.
    if (this.fuel <= 0) {
      engineForce = 0;
      brake = config.brakeForce;
      this.braking = true;
      this.reversing = false;
    }

    // Steering tightens as speed rises, like a real rack.
    const speedAbs = Math.abs(forwardSpeed);
    const limit = config.steerMax / (1 + speedAbs * config.steerSpeedFalloff);
    const target = steerInput * limit * config.steerSign;
    this.steer = damp(this.steer, target, config.steerResponse, dt);

    const wheelCount = this.vehicle.getNumWheels();
    for (let i = 0; i < wheelCount; i += 1) {
      const wheelConfig = config.wheels[i];

      if (wheelConfig.front) this.vehicle.setSteeringValue(this.steer, i);
      if (this.drivenWheels.includes(i)) this.vehicle.applyEngineForce(engineForce, i);

      if (handbrake && !wheelConfig.front) {
        this.vehicle.setBrake(config.handbrakeForce, i);
      } else {
        this.vehicle.setBrake(brake, i);
      }
    }
  }

  respawn() {
    this.body.teleport(this.spawn, new Quaternion().setFromAxisAngle(UP, this.initialYaw));
    this.steer = 0;
    this.spin = 0;
    this.speed = 0;
    this.speedKmh = 0;
    this.heading = this.initialYaw;
    this.reversing = false;
    this.braking = false;
    this._flippedTime = 0;
    this._autoPedal = 0;

    const wheelCount = this.vehicle.getNumWheels();
    for (let i = 0; i < wheelCount; i += 1) {
      this.vehicle.applyEngineForce(0, i);
      this.vehicle.setBrake(0, i);
      this.vehicle.setSteeringValue(0, i);
    }
    this.vehicle.resetSuspension();

    // The teleport is instantaneous; discard the interpolation history so the
    // truck does not glide from its old position to the spawn point.
    this.snapVisualState();
    this.smoke?.reset();
    this.onRespawn?.();
  }

  lateUpdate(dt) {
    if (this.body.disposed) return;

    const position = this.mesh.position;
    if (position.y < this.config.fallResetY) {
      this.respawn();
      return;
    }

    // Speed + heading for the camera and HUD.
    this.body.getLinearVelocity(this._velocity);
    this._forward.set(0, 0, 1).applyQuaternion(this.mesh.quaternion);
    const forwardSpeed = this._velocity.dot(this._forward);

    this.speed = forwardSpeed;
    this.speedKmh = Math.abs(forwardSpeed) * 3.6;
    this.heading = Math.atan2(this._forward.x, this._forward.z);

    // The wheel roll is cosmetic and integrated here so it advances with the
    // fixed physics clock, then gets interpolated for display along with the
    // suspension height.
    this.spin += (this.speed / this.config.wheel.radius) * dt;

    this.focus.position.copy(position);
    this.focus.heading = this.heading;
    this.focus.speed = forwardSpeed;

    this.#captureVisualState();
    this.#syncLights();
  }

  /**
   * Blend the last two fixed-step poses onto the visible mesh. Called once per
   * rendered frame with `Loop`'s interpolation alpha, or with 1 to pin the
   * visuals to the newest pose when rendering is locked to the simulation. The
   * physics body and the gameplay checks that run in the fixed step stay at the
   * current pose.
   */
  render(alpha = 1, dt = 0) {
    if (this.body.disposed) return;

    const t = Number.isFinite(alpha) ? clamp(alpha, 0, 1) : 1;
    this.mesh.position.lerpVectors(this._prevPosition, this._currPosition, t);
    this.mesh.quaternion.slerpQuaternions(
      this._prevQuaternion,
      this._currQuaternion,
      t,
    );

    this.focus.position.copy(this.mesh.position);
    this.focus.speed = lerp(this._prevSpeed, this._currSpeed, t);
    this.focus.heading =
      this._prevHeading + shortestAngle(this._prevHeading, this._currHeading) * t;

    this.#applyWheelVisuals(t);
    this.smoke.update(dt, this);
  }

  /**
   * Drop the interpolation history and pin the visuals to the current body
   * pose. Used after boot and after a respawn/teleport.
   */
  snapVisualState() {
    if (this.body.disposed) return;

    this.body.getPosition(this._currPosition);
    this.body.getRotation(this._currQuaternion);
    this.#captureWheelTravel();

    this._prevPosition.copy(this._currPosition);
    this._prevQuaternion.copy(this._currQuaternion);
    for (let i = 0; i < this._currWheelY.length; i += 1) {
      this._prevWheelY[i] = this._currWheelY[i];
    }
    this._prevSpin = this.spin;
    this._currSpin = this.spin;
    this._prevSteer = this.steer;
    this._currSteer = this.steer;
    this._prevSpeed = this.speed;
    this._currSpeed = this.speed;
    this._prevHeading = this.heading;
    this._currHeading = this.heading;

    this.mesh.position.copy(this._currPosition);
    this.mesh.quaternion.copy(this._currQuaternion);
    this.focus.position.copy(this._currPosition);
    this.focus.heading = this.heading;
    this.focus.speed = this.speed;
    this.#applyWheelVisuals(1);
  }

  /**
   * Shift the previous pose aside and record the pose Bullet just produced.
   * Runs at the end of the fixed step, after `physics.step()`.
   */
  #captureVisualState() {
    this._prevPosition.copy(this._currPosition);
    this._prevQuaternion.copy(this._currQuaternion);
    for (let i = 0; i < this._currWheelY.length; i += 1) {
      this._prevWheelY[i] = this._currWheelY[i];
    }
    this._prevSpin = this._currSpin;
    this._prevSteer = this._currSteer;
    this._prevSpeed = this._currSpeed;
    this._prevHeading = this._currHeading;

    this.body.getPosition(this._currPosition);
    this.body.getRotation(this._currQuaternion);
    this.#captureWheelTravel();

    this._currSpin = this.spin;
    this._currSteer = this.steer;
    this._currSpeed = this.speed;
    this._currHeading = this.heading;
  }

  /** Read the suspension height of every wheel in chassis-local space. */
  #captureWheelTravel() {
    if (!this.vehicle) return;

    this._inverseQuat.copy(this._currQuaternion).invert();

    const wheelCount = this.vehicle.getNumWheels();
    for (let i = 0; i < wheelCount; i += 1) {
      const transform = this.vehicle.getWheelTransformWS(i);
      const origin = transform.getOrigin();
      this._wheelWorld.set(origin.x(), origin.y(), origin.z());
      this._wheelLocal
        .copy(this._wheelWorld)
        .sub(this._currPosition)
        .applyQuaternion(this._inverseQuat);

      this._currWheelY[i] = this._wheelLocal.y;
    }
  }

  #applyWheelVisuals(t) {
    const spin = lerp(this._prevSpin, this._currSpin, t);
    const steer = lerp(this._prevSteer, this._currSteer, t);

    for (let i = 0; i < this.wheels.length; i += 1) {
      const wheelConfig = this.config.wheels[i];
      const wheel = this.wheels[i];

      // The wheel hardpoints are fixed in chassis space, so keep X/Z pinned and
      // only take the suspension height from Bullet. This avoids one-frame
      // fore/aft shimmer between the wheels and the body at speed.
      wheel.position.set(
        wheelConfig.x,
        lerp(this._prevWheelY[i], this._currWheelY[i], t),
        wheelConfig.z,
      );

      // Steering around Y first, then rolling around the wheel's own axle (X).
      wheel.rotation.set(spin, wheelConfig.front ? steer : 0, 0, 'YXZ');
    }
  }

  #syncLights() {
    this.lights.brake.emissiveIntensity = this.braking || this.reversing ? 1.4 : 0.14;
    this.lights.reverse.emissiveIntensity = this.reversing ? 0.9 : 0.08;
  }

  dispose() {
    this.smoke?.dispose();
    this.smoke = null;

    // The action must leave the world before its chassis body is destroyed.
    if (this.vehicle) {
      this.physics.removeAction(this.vehicle);
      this.physics.Ammo.destroy(this.vehicle);
      this.vehicle = null;
    }
    if (this.raycaster) {
      this.physics.Ammo.destroy(this.raycaster);
      this.raycaster = null;
    }
    if (this.tuning) {
      this.physics.Ammo.destroy(this.tuning);
      this.tuning = null;
    }
  }
}
